import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  ApiButton,
  Box,
  Button,
  ContentTemplate,
  ErrorInfo,
  nameRegExp,
  SectionHeader,
  TextFieldForm,
  Typography,
} from 'web-library';

import { makeStyles } from 'tss-react/mui';
import { AxiosError } from 'axios';
import { AUTHORITY_NAMES } from 'web-library/models/AuthorityFunctions';
import { GenericErrorResponse, KeyValueResponse } from '../../../../api-refactored/typings';
import ControlledAutocomplete from '../../../../components/form-items/ControlledAutocomplete';
import { useParticularNonNationalAeis } from '../../logic/useParticularNonNationalExam';
import { ParentParticularDetails } from '../../../../api/candidate/exam/model';
import { ParentDetailTypesResponse } from '../../../../api-refactored/non-national-exam-registration/useGetParentDetailTypes';
import { WorkPassType } from '../../../../api-refactored/candidate/useGetWorkPassTypes';
import { UserCitizenShipsResponse } from '../../../../api-refactored/non-national-exam-registration/useGetUserCitizenShips';
import ParentDetailForm from './ParentDetailForm';
import { useGetUserAuthorities } from '../../../../api-refactored/user/useGetUserAuthorities';
import { existInArray } from '../../../../utils/ArrayUtils';

export const docType = [
  { description: 'NRIC', code: 'I', disabled: true },
  { description: 'Passport No.', code: 'P' },
  { description: 'N/A', code: 'NOTAPPLICABLE' },
];

const useStyles = makeStyles()((theme) =>
({
  container: {
    marginTop: theme.spacing(-2),
  },
  titleStatement: {
    marginBottom: theme.spacing(4),
  },
  inputField: {
    marginBottom: theme.spacing(6),
  },
  inputSection: {
    marginBottom: theme.spacing(4),
  },
  sectionWrapper: {},
  sectionTitle: {
    marginBottom: theme.spacing(2),
  },
  sectionSubTitleWrapper: {
    marginBottom: theme.spacing(4),
  },
  actionButton: {
    marginLeft: theme.spacing(2),
    [theme.breakpoints.down('sm')]: {
      height: 'fit-content',
      marginLeft: theme.spacing(0),
      marginTop: theme.spacing(2),
      marginRight: theme.spacing(9),
    },
  },
  errMsg: {
    marginTop: '0 !important',
  },
  radioGroup: {
    marginBottom: theme.spacing(4),
  },
  buttonContainer: {
    paddingBottom: theme.spacing(10),
  },
  errString: {
    color: theme.color.state.stateError,
    marginBottom: theme.spacing(2),
  },
}));

interface ParentParticularsAeisExamProps {
  isOpsAdmin?: boolean;
  onCancel: () => void;
  applicationUuid: string;
  parentParticularDetails: ParentParticularDetails | undefined;
  parentDetails: ParentDetailTypesResponse[] | undefined;
  isLoadingParentDetails: boolean;
  errorParentDetails: AxiosError<GenericErrorResponse> | null;
  refetchParentDetails: () => void;
  workPassTypes: WorkPassType[] | undefined;
  citizenShips: UserCitizenShipsResponse[] | undefined;
}

export enum TypeParent {
  Mother = 'Mother',
  Father = 'Father',
  MotherAndFather = 'Father and Mother',
}

export enum CitizenType {
  SC = 'SC',
  PR = 'PR',
  OTHERS = 'OTHERS',
  NA = 'NOTAPPLICABLE',
}

const ParentParticularsAeisExam = ({
  isOpsAdmin,
  onCancel,
  applicationUuid,
  parentParticularDetails,
  parentDetails,
  isLoadingParentDetails,
  errorParentDetails,
  refetchParentDetails,
  workPassTypes,
  citizenShips,
}: ParentParticularsAeisExamProps) => {
  const { t } = useTranslation();
  const { classes } = useStyles();

  const [errorMsg, setErrorMsg] = useState<string>('');

  const {
    form,
    submitContinue,
    status,
    formDefaultValues,
    nextPage,
  } = useParticularNonNationalAeis({
    applicationUuid,
    parentParticularDetails,
    parentDetails,
    workPassTypes,
    citizenShips,
    setErrorMsg,
  });

  const { formState: { errors }, control, watch } = form;
  const { parentType: watchParentType } = watch();
  const authorities = useGetUserAuthorities();
  const hasViewAccess = isOpsAdmin
    ? existInArray(authorities.data, AUTHORITY_NAMES.CP_ADMIN_NON_NATIONAL_APPLICATIONS_REVIEW_VIEW)
    : true;
  const hasEditAccess = isOpsAdmin
    ? existInArray(authorities.data, AUTHORITY_NAMES.CP_ADMIN_NON_NATIONAL_APPLICATIONS_REVIEW_EDIT)
    : true;

  return (
    <form
      onSubmit={form.handleSubmit(
        (formValue) => {
          setErrorMsg('');
          submitContinue(formValue);
        },
        () => {
          if (!hasEditAccess && hasViewAccess) {
            nextPage();
          }
        }
      )}
    >
      <ContentTemplate isDividerBottom>
        <SectionHeader title={t('exam->>ParentParticulars.title')} />
        {!isOpsAdmin && (
          <Typography className={classes.titleStatement} variant="body1">
            {t('exam->>ParentParticulars.TitleStatement')}
          </Typography>
        )}
        <TextFieldForm
          id="input-name"
          className={classes.inputField}
          label={t('exam->>ParentParticulars.Name')}
          isRequired
          form={form}
          name="name"
          type="text"
          error={!!errors?.name}
          helperText={errors?.name?.message?.toString()}
          placeholder="Enter name"
          disabled={!hasEditAccess}
          regExpToBlock={nameRegExp}
        />
        <TextFieldForm
          id="input-nricFinPassport"
          className={classes.inputField}
          label={t('exam->>ParentParticulars.NricFin')}
          onChange={(e) => form.setValue('uinFinPassport', e.target.value.toUpperCase())}
          isRequired
          form={form}
          name="uinFinPassport"
          type="text"
          error={!!errors?.uinFinPassport}
          helperText={errors?.uinFinPassport?.message?.toString()}
          disabled={!hasEditAccess}
        />
      </ContentTemplate>
      <Box className={classes.container}>
        <ContentTemplate isDividerBottom={!!watchParentType} continuous>
          <Typography variant="h3" className={classes.sectionTitle}>
            {t('exam->>ParentParticulars.parentParticulars')}
          </Typography>
          <Box className={classes.sectionSubTitleWrapper}>
            <Typography variant="body1">{t('exam->>ParentParticulars.subTitle')}</Typography>
          </Box>
          <ControlledAutocomplete
            isRequired
            id="inputSelect-parentDetails"
            loading={isLoadingParentDetails}
            loadingError={!!errorParentDetails}
            loadingErrorOnReFetch={refetchParentDetails}
            name="parentType"
            control={control}
            label={t('exam->>ParentParticulars.parentDetails')}
            options={parentDetails || []}
            getOptionLabel={(option) => (typeof option === 'string' ? option : option.description)}
            isOptionEqualToValue={(option: KeyValueResponse, value: KeyValueResponse) =>
              option?.code === value.code
            }
            className={classes.inputField}
            placeholder="Select parent details"
            error={!!errors?.parentType}
            helperText={errors?.parentType?.message?.toString()}
            disableClearable
            disabled={!hasEditAccess}
            readOnly
          />
        </ContentTemplate>
      </Box>
      <Box className={classes.container}>
        <ParentDetailForm
          citizenShips={citizenShips}
          classes={classes}
          form={form}
          formDefaultValues={formDefaultValues}
          workPassTypes={workPassTypes}
          hasEditAccess={hasEditAccess}
        />
        <ContentTemplate continuous>
          {parentParticularDetails?.lockedByUser && (
            <Typography variant="body2" className={classes.errString}>
              {t('exam->>NonNationalExam.LockedMessage', {
                lockedByUser: parentParticularDetails?.lockedByUser,
              })}
            </Typography>
          )}
          <ErrorInfo className={classes.errString} label={errorMsg} />
          <Box className={classes.buttonContainer}>
            <Button
              id="button-applicant-declaration-back"
              type="button"
              variant="outlined"
              size="medium"
              color="secondary"
              onClick={onCancel}
            >
              Back
            </Button>
            <ApiButton
              id="button-confirm-parent-particulars"
              className={classes.actionButton}
              status={status}
              type="submit"
              color="secondary"
              size="medium"
            >
              Confirm Parent Particulars
            </ApiButton>
          </Box>
        </ContentTemplate>
      </Box>
    </form>
  );
};

export default ParentParticularsAeisExam;
